import { createContext, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGroupProfileQuery } from "../../App/Queries/GroupQueries";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { useAppSelector } from "../../App/State/Hooks/reduxHooks";
import { useClientChangeCallback } from "../../App/State/Hooks/useClientChangeCallback";
import { selectClientValue } from "../../App/State/Slices/SelectedClientSlice";
import { IGroup } from "../../App/Typings/Groups";

interface IGroupProfileContext {
  group: IGroup;
  isLoading: boolean;
}

const defaultGroupProfileContext = {
  group: {} as IGroup,
  isLoading: false,
};

export const GroupProfileContext = createContext<IGroupProfileContext>(defaultGroupProfileContext);

export function GroupProfileProvider({ children }: { children: React.ReactNode }) {
  useClientChangeCallback("/candidategroups");
  const { clientId } = useAppSelector(selectClientValue);
  const navigate = useNavigate();
  const { displayError } = useNotification();
  const { id = "" } = useParams();
  const { data: group, error, isLoading } = useGroupProfileQuery(id);

  const placeholderGroup: IGroup = {
    id: "",
    name: "",
    clientId,
    memberCount: 0,
    createdDate: "",
    creatorId: "",
    creatorName: "",
    creatorNickname: "",
    creatorUsername: "",
    lastUpdateDate: "",
    isDynamic: false,
    rules: [],
    isArchived: false,
    userIdArchived: null,
    archivedDate: null,
  };

  useEffect(() => {
    if (error) {
      displayError("Error fetching group details");
      navigate("/candidategroups");
    } else if (!isLoading && group && group.clientId !== clientId) {
      navigate("/candidategroups");
    }
  }, [error, displayError, navigate, group, isLoading, clientId]);

  return (
    <GroupProfileContext.Provider
      value={{
        group: group ?? placeholderGroup,
        isLoading,
      }}
    >
      {children}
    </GroupProfileContext.Provider>
  );
}

export const useGroupProfileContext = () => useContext(GroupProfileContext);
